// ---------------------------------------------------------------------------
// FAO-56 Hargreaves-Samani reference evapotranspiration — a pure
// meteorological formula (Allen et al. 1998) with no dependency on AEMET,
// ROCIO, or any other data-source concept. Used to live inside aemet.ts,
// purely because that was the first module that needed it — but rocio.ts (a
// separate, unrelated grid-cell domain) reached into aemet.ts for the exact
// same formula to build its own ETo series. Kept here, in its own module, so
// both (and anything else that just needs "ETo from tmax/tmin/tmed/lat/day")
// don't have to depend on the AEMET/series machinery to get it (same
// reasoning as isoDate.ts/geo.ts).
// ---------------------------------------------------------------------------

/**
 * ETP (Hargreaves-Samani, FAO-56 eq. 52 — Allen et al. 1998,
 * fao.org/4/x0490e/x0490e07.htm): `ETo = 0.0023*(Tmed+17.8)*sqrt(Tmax-Tmin)*Ra`,
 * with Ra (extraterrestrial radiation) already expressed in mm/day
 * equivalent. Doesn't need measured solar radiation/humidity/wind — only
 * tmax/tmin/tmed.
 *
 * `Math.max(0, tmax - tmin)` before the square root — real incident
 * (2026-08-27, a real Júcar-basin ROCIO import): `tmax < tmin` by a
 * hundredth of a degree on one single day, for a handful of ROCIO grid
 * cells (4 of 16156 checked on that one date) — rounding noise at the
 * source data's own 2-decimal precision, not a genuine temperature
 * inversion. `Math.sqrt` of that tiny negative amplitude returns `NaN` in
 * JS, and since this feeds directly into a sub-basin's own ETP series, one
 * such day poisoned the WHOLE downstream calculation for that sub-basin —
 * `engine.ts`'s truncation logic cuts off every step from the first
 * invalid one onward, throwing away decades of otherwise good data over a
 * single day of measurement noise. Clamping to 0 gives that one day a
 * near-zero (not wrong) ETo instead — a essentially-zero diurnal range
 * genuinely implies very little radiative drive for evapotranspiration,
 * so this is the physically sensible value here, not just "make the NaN go
 * away". Applies to station-based ETP too (aemet.ts's two call sites) —
 * real station tmax/tmin readings can have the exact same near-isothermal/
 * rounding edge case.
 *
 * Rounded to 2 decimals (mm/day) — this is a CALCULATED value, not a
 * measurement, so plain floating-point arithmetic through 5 chained
 * operations otherwise leaves it carrying a dozen+ meaningless decimal
 * digits (e.g. `3.8412907734918263`) everywhere it's used: the calculation
 * engine, charts, tables, CSV exports. 2 decimals matches the real
 * precision AEMET's own raw tmax/tmin/precipitation values already carry
 * (see prepare-rocio-grid.cjs/prepare-rocio-temperature.cjs's own header
 * comments) — this derived value shouldn't claim to be more precise than
 * the inputs it came from.
 */
export function hargreavesEto(
  tmax: number,
  tmin: number,
  tmed: number,
  latDeg: number,
  dayOfYearValue: number
): number {
  const ra = extraterrestrialRadiationMm(latDeg, dayOfYearValue);
  const eto = 0.0023 * (tmed + 17.8) * Math.sqrt(Math.max(0, tmax - tmin)) * ra;
  return Math.round(eto * 100) / 100;
}

/**
 * Extraterrestrial radiation Ra, in mm/day equivalent (FAO-56 eq. 21, times
 * the 0.408 MJ->mm conversion of eq. 20). `latDeg` positive north (Spain's
 * whole range, ~36-44°N, stays well inside the domain where the sunset-hour-
 * angle formula (eq. 25) is valid — no polar-latitude edge case to guard
 * against here).
 */
export function extraterrestrialRadiationMm(latDeg: number, dayOfYearValue: number): number {
  const latRad = (latDeg * Math.PI) / 180;
  const dr = 1 + 0.033 * Math.cos((2 * Math.PI * dayOfYearValue) / 365); // eq. 23
  const decl = 0.409 * Math.sin((2 * Math.PI * dayOfYearValue) / 365 - 1.39); // eq. 24
  const sunsetAngle = Math.acos(-Math.tan(latRad) * Math.tan(decl)); // eq. 25
  const solarConstant = 0.082; // MJ m-2 min-1
  const raMJ =
    ((24 * 60) / Math.PI) *
    solarConstant *
    dr *
    (sunsetAngle * Math.sin(latRad) * Math.sin(decl) +
      Math.cos(latRad) * Math.cos(decl) * Math.sin(sunsetAngle)); // eq. 21
  return 0.408 * raMJ;
}
